import React, { useState, useEffect } from 'react';
import axiosClient, { UPLOADS_BASE_URL } from '../../axios_client';
import AdminMainLayout from './AdminMainLayout';
import AddAdminEvents from './AddAdminEvents';
import UpdateAdminEvents from './UpdateAdminEvents';

const AdminEvents = () => {
    const [events, setEvents] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [error, setError] = useState('');
    const [expandedEvent, setExpandedEvent] = useState(null);

    // Define base URL for images (adjust as needed for production)
    const BASE_URL = UPLOADS_BASE_URL; // Use https://sunflower-resort.com for production

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axiosClient.get('/events', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setEvents(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch events');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            try {
                await axiosClient.delete(`/events/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                fetchEvents();
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to delete event');
            }
        }
    };

    const openUpdateModal = (event) => {
        setSelectedEvent(event);
        setIsUpdateModalOpen(true);
    };

    const toggleEvent = (id) => {
        setExpandedEvent(expandedEvent === id ? null : id);
    };

    // Normalize image URL to ensure it starts with a slash
    const getImageUrl = (img) => {
        const path = img.startsWith('/') ? img : `/${img}`;
        return `${BASE_URL}${path}`;
    };

    return (
        <AdminMainLayout>
            <div className="min-h-screen bg-[#F3ECDE] p-4 sm:p-6">
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-[#12392B]">
                    Manage Events
                </h1>
                {error && (
                    <p className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4">
                        {error}
                    </p>
                )}
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mb-4 bg-[#12392B] text-white p-2 sm:p-3 rounded hover:bg-[#E6A71A] hover:text-[#12392B] transition text-sm sm:text-base"
                >
                    Add New Event
                </button>

                {/* Small Screen: Collapsible Cards */}
                <div className="md:hidden">
                    {events.map((event) => (
                        <div
                            key={event.id}
                            className="bg-white p-4 rounded-lg shadow-md mb-4"
                        >
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={() => toggleEvent(event.id)}
                            >
                                <h2 className="text-lg sm:text-xl font-semibold text-[#12392B]">
                                    {event.event_name}
                                </h2>
                                <svg
                                    className="w-5 h-5 text-[#12392B]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d={
                                            expandedEvent === event.id
                                                ? 'M18 12H6'
                                                : 'M12 5v14m7-7H5'
                                        }
                                    />
                                </svg>
                            </div>
                            {expandedEvent === event.id && (
                                <div className="mt-3">
                                    <p className="text-gray-600 text-sm">{event.description}</p>
                                    {event.images && event.images.length > 0 ? (
                                        <div className="mt-2 space-y-2">
                                            {event.images.map((img, index) => (
                                                <img
                                                    key={index}
                                                    src={getImageUrl(img)}
                                                    alt={`Event ${index}`}
                                                    className="w-auto h-20 object-cover rounded"
                                                    onError={(e) => {
                                                        console.error(`Failed to load image: ${getImageUrl(img)}`);
                                                        e.target.src = '/fallback-image.jpg'; // Fallback image
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500 text-sm mt-2">
                                            No images available
                                        </p>
                                    )}
                                    <div className="mt-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(event)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(event.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Medium and Larger Screens: Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-md">
                        <thead>
                            <tr className="bg-[#12392B] text-white">
                                <th className="p-3 text-left text-sm font-semibold">Event Name</th>
                                <th className="p-3 text-left text-sm font-semibold">
                                    Description
                                </th>
                                <th className="p-3 text-left text-sm font-semibold">Images</th>
                                <th className="p-3 text-left text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {events.map((event) => (
                                <tr key={event.id} className="border-b">
                                    <td className="p-3 text-[#12392B]">{event.event_name}</td>
                                    <td className="p-3 text-gray-600">{event.description}</td>
                                    <td className="p-3">
                                        {event.images && event.images.length > 0 ? (
                                            <div className="flex space-x-2">
                                                {event.images.map((img, index) => (
                                                    <img
                                                        key={index}
                                                        src={getImageUrl(img)}
                                                        alt={`Event ${index}`}
                                                        className="w-auto h-20 object-cover rounded"
                                                        onError={(e) => {
                                                            console.error(`Failed to load image: ${getImageUrl(img)}`);
                                                            e.target.src = '/fallback-image.jpg'; // Fallback image
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-500">No images</span>
                                        )}
                                    </td>
                                    <td className="p-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(event)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(event.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <AddAdminEvents
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onEventAdded={fetchEvents}
                />
                <UpdateAdminEvents
                    isOpen={isUpdateModalOpen}
                    onClose={() => setIsUpdateModalOpen(false)}
                    event={selectedEvent}
                    onEventUpdated={fetchEvents}
                />
            </div>
        </AdminMainLayout>
    );
};

export default AdminEvents;